import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createLead, getLocations } from "../services/api";
import { LEAD_SOURCES, LEAD_STATUSES, LEAD_TYPES } from "../utils/displayFormatters";

const LeadCreate = ({ authToken }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    status: "",
    type: "",
    source: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
    },
  });
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      setIsLoading(true);
      try {
        const fetchedLocations = await getLocations(authToken);
        console.log("Fetched locations:", fetchedLocations);
        setLocations(fetchedLocations);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setError("Failed to fetch locations. Please try again.");
        setIsLoading(false);
      }
    };
    fetchLocations();
  }, [authToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]:
          name === "location"
            ? value === ""
              ? ""
              : parseInt(value, 10)
            : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.location === "") {
      setError("Please select a location");
      return;
    }
    if (formData.source === "") {
      setError("Please select a source");
      return;
    }
    try {
      const submitData = {
        ...formData,
        location_id: formData.location ? parseInt(formData.location, 10) : null,
        source: formData.source || null,
        status: formData.status || null,
        type: formData.type || null,
        // Clean up address - only send if any field has content
        address: Object.values(formData.address).some(val => val.trim()) 
          ? formData.address 
          : null,
      };
      // Remove the old location field to avoid confusion
      delete submitData.location;
      console.log("Submitting form data:", submitData);
      await createLead(authToken, submitData);
      navigate("/leads");
    } catch (error) {
      console.error("Error creating lead:", error);
      if (error.response && error.response.data) {
        console.log("Server response:", error.response.data);
        // Try to extract meaningful error from the HTML response
        if (typeof error.response.data === 'string' && error.response.data.includes('IntegrityError')) {
          setError("Database constraint violation. Please check that all required fields are filled correctly and try again.");
        } else {
          setError("Failed to create lead: Server error (500). Please try again.");
        }
      } else {
        setError("Failed to create lead. Please try again.");
      }
    }
  };
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 bg-background">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-3xl font-bold text-gray-800 flex items-center space-x-2 mb-4 sm:mb-0">
          Create New Lead
        </h3>
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6"
            role="alert"
          >
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <button
          onClick={() => navigate("/leads")}
          className="inline-flex items-center px-4 py-2 border border-text-primary text-primary rounded-lg hover:bg-text-primary hover:text-white focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Leads
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-sm rounded-lg p-8 mb-4 hover:border hover:border-teal-200 transition duration-300"
      >
        {/* Personal Information Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-border flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="first_name"
              >
                First Name
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="first_name"
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="last_name"
              >
                Last Name
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="last_name"
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="email"
              >
                Email
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="phone"
              >
                Phone
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        {/* Lead Details Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-border flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Lead Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="location"
              >
                Location
              </label>
              <select
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              >
                <option value="">Select a location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="status"
              >
                Status
              </label>
              <select
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="">Select a status</option>
                {LEAD_STATUSES.map((status) => (
                  <option key={status.key} value={status.key}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="type"
              >
                Type
              </label>
              <select
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="">Select a type</option>
                {LEAD_TYPES.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="source"
              >
                Source
              </label>
              <select
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                required
              >
                <option value="">Select a Lead Source</option>
                {LEAD_SOURCES.map((source) => (
                  <option key={source.key} value={source.key}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-border flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-orange-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Address Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="address.street"
              >
                Street
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="address.street"
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="address.city"
              >
                City
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="address.city"
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="address.state"
              >
                State
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="address.state"
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="address.country"
              >
                Country
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="address.country"
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
              />
            </div>
            <div>
              <label
                className="block text-gray-600 text-sm font-medium mb-2"
                htmlFor="address.postal_code"
              >
                Postal Code
              </label>
              <input
                className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
                id="address.postal_code"
                type="text"
                name="address.postal_code"
                value={formData.address.postal_code}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            className="bg-secondary hover:bg-secondary/90/85 text-white font-medium py-2 px-6 rounded-lg focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
            type="submit"
          >
            Create Lead
          </button>
        </div>
      </form>
    </div>
  );

};

export default LeadCreate;
